import { OrgIcon } from '@/components/Sidebar/components/OrgSelector/constants';
import { authQueryKeys, orgQueryKeys } from '@/config/queryKeys';
import {
  INITIAL_AUTH_GATE_IDLE_RECHECK_MS,
  INITIAL_AUTH_GATE_RECHECK_MS,
  RECENT_LOGOUT_SUPPRESSION_MS,
  adminRoles,
  isPostLoginCallback,
  isPublicRoute,
} from '@/constants';
import { useAuthStatus, useInitialAuthGate } from '@/hooks';
import type { InitialAuthGateState } from '@/hooks/useInitialAuthGate';
// Handle post-login redirect
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { useAppStore } from '@/stores/app';
import { SUPER_ADMIN_ROLE } from '@/types';
import type { AuthData } from '@/types/auth';
import { AuthCheckFailed, FullLoading, Layout, Sidebar } from '@components/index';
import { mainContent, mainContentWithSidebar } from '@components/Layout/styles.module.css';
import type { SidebarLinkProps } from '@components/Sidebar/types';
import { Toaster } from '@nautilusbio/nb-design-system/Toaster';
import { VoyagerIcon } from '@nautilusbio/nb-design-system/VoyagerIcon';
import { TanStackDevtools } from '@tanstack/react-devtools';
import '@/styles/components/ag-grid-overrides.css';
import { FormDevtoolsPanel } from '@tanstack/react-form-devtools';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  redirect,
  useLocation,
} from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel, Separator, usePanelRef } from 'react-resizable-panels';
import { useShallow } from 'zustand/react/shallow';

import globalStyles from '../styles/index.module.css';
import pageStyles from '../styles/pages/index.module.css';

type RouterContext = {
  authentication: AuthData;
  queryClient: QueryClient;
};

const tanstackPlugins = [
  {
    name: 'TanStack Query',
    render: <ReactQueryDevtoolsPanel />,
    defaultOpen: false,
  },
  {
    name: 'TanStack Router',
    render: <TanStackRouterDevtoolsPanel />,
    defaultOpen: false,
  },
  {
    name: 'TanStack Form',
    render: <FormDevtoolsPanel theme="dark" />,
    defaultOpen: false,
  },
];
const tanstackConfig = { hideUntilHover: true };
const toastOptions = {
  duration: 2000,
};
// TODO: Error/404 Handling

function mainPanelContent(gateState: InitialAuthGateState, postLoginCallback: boolean, loadingLabel: string) {
  if (gateState === 'timed-out') return <AuthCheckFailed />;
  // The post-login window renders through while still `checking`: its cookie-propagation poller
  // lives inside <Outlet />, so withholding it there would suppress the recovery being waited on.
  if (gateState === 'checking' && !postLoginCallback) return <FullLoading label={loadingLabel} />;
  return <Outlet />;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: ({ context }) => {
    // /auth/me is disabled on every public route, so UNAUTHENTICATED here is
    // the query's off-state — redirecting on it strands /reset-password.
    if (isPublicRoute(window.location.pathname)) {
      return;
    }

    // Skip redirect during the post-login callback — the session cookie may
    // not yet be visible to the first /auth/me probe.
    if (isPostLoginCallback(window.location.pathname, window.location.search)) {
      return;
    }

    // Must wait for the auth query to complete before deciding — see
    // docs/ROUTE_PROTECTION.md's "Critical: beforeLoad Hook Order".
    if (context.authentication.status === 'PENDING') {
      return;
    }

    // Don't call clearClientSession() here — status can be transiently
    // UNAUTHENTICATED during refetches/org switches; router.tsx's 401
    // handler owns session cleanup.
    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({ to: '/login' });
    }
  },
  component: () => {
    const location = useLocation();

    const showSidebar = useMemo(() => !isPublicRoute(location.pathname), [location?.pathname]);
    // Same window.location source useAuthStatus reads to decide whether /auth/me
    // is enabled at all, so the gate can never disagree with the query it waits on.
    const onPublicRoute = isPublicRoute(window.location.pathname);
    const postLoginCallback = isPostLoginCallback(window.location.pathname, window.location.search);

    const { user, status, userQuery } = useAuthStatus();
    // The watchdog stays armed through the post-login window even though <Outlet /> renders there,
    // because that window has its own permanent-spinner state (src/routes/index.tsx) to escape.
    const gateState = useInitialAuthGate(status, { enabled: !onPublicRoute });
    const { setUser, selectedOrganization, selectedOrgStub, setSelectedOrganization } = useAppStore(
      useShallow(s => ({
        setUser: s.setUser,
        selectedOrganization: s.selectedOrganization,
        selectedOrgStub: s.selectedOrgStub,
        setSelectedOrganization: s.setSelectedOrganization,
      })),
    );

    useEffect(() => {
      setUser(user ?? undefined);
    }, [user, setUser]);

    const { data: orgs } = useQuery({
      enabled: !!user,
      queryKey: orgQueryKeys.list(),
      queryFn: fetchOrganizations,
    });

    // Re-hydrate selectedOrganization from its persisted stub after a reload
    // or clearClientSession — otherwise OrgSelector/InstrumentFormDialog have
    // no org on any route besides "/".
    useEffect(() => {
      if (!selectedOrganization && orgs && orgs.length > 0) {
        const targetId = selectedOrgStub?.id;
        const org = targetId ? (orgs.find(o => o.id === targetId) ?? orgs[0]) : orgs[0];
        setSelectedOrganization(org);
      }
    }, [orgs, selectedOrganization, selectedOrgStub, setSelectedOrganization]);

    const sidebarDisabled = !!user && orgs && orgs.length === 0;

    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isHovering, setIsHovering] = useState<boolean>(false);
    const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const didMountRef = useRef(false);
    const { t } = useTranslation('sidebar');
    const { t: tLogin } = useTranslation('login');

    usePostLoginRedirect();

    const queryClient = useQueryClient();

    // The gate has to be able to open itself: on a deep link after login nothing else re-probes,
    // and the auth query sets retry: false with its next refetch 5 minutes out. Keeps probing past
    // the watchdog (backed off) so a late session still recovers inside that cycle.
    useEffect(() => {
      if (gateState === 'resolved') return undefined;

      // Backs off once the watchdog has given up: past that point the user has an explicit Try
      // Again, and holding the tight loop would poll an already-failing /auth/me indefinitely.
      const intervalMs = gateState === 'timed-out' ? INITIAL_AUTH_GATE_IDLE_RECHECK_MS : INITIAL_AUTH_GATE_RECHECK_MS;

      const recheck = setInterval(() => {
        // cancelRefetch defaults to true, and once /auth/me has resolved to `null` (a 401 resolves
        // rather than throwing) that default aborts the in-flight probe the gate is waiting on,
        // livelocking it whenever the round trip is slower than the interval.
        void queryClient.invalidateQueries({ queryKey: authQueryKeys.me() }, { cancelRefetch: false });
      }, intervalMs);
      return () => clearInterval(recheck);
    }, [gateState, queryClient]);

    const authDiagnosticsRef = useRef({ status, userQuery });
    useEffect(() => {
      authDiagnosticsRef.current = { status, userQuery };
    }, [status, userQuery]);

    // The only diagnostic this state leaves behind — useAuthStatus collapses every path here to
    // UNAUTHENTICATED, so the query's error plus the storage stamps are what tell them apart.
    // Keyed on gateState alone so the refetch cycle can't re-log one incident repeatedly.
    useEffect(() => {
      if (gateState !== 'timed-out') return;

      const { status: authStatus, userQuery: query } = authDiagnosticsRef.current;
      // Guarded: this screen is reachable *because* storage may be broken, and an unguarded read
      // would throw out of the effect that exists to report the failure.
      const readStamp = (key: string) => {
        try {
          return sessionStorage.getItem(key);
        } catch {
          return '<unreadable>';
        }
      };
      console.error('[auth-gate] gave up waiting for the first /auth/me result', {
        authStatus,
        pathname: window.location.pathname,
        queryError: query?.error?.message,
        fetchStatus: query?.fetchStatus,
        failureCount: query?.failureCount,
        // The only tells for a suppressed redirect, which is otherwise indistinguishable here
        // from a merely slow probe (both leave no query error and no failures).
        navLockUntil: readStamp('__nav_lock_until'),
        lastLogoutTs: readStamp('__last_logout_ts'),
      });
    }, [gateState]);

    // Proactive auth revalidation on tab focus.
    // Uses both 'visibilitychange' and 'focus' because some browsers (e.g. mobile Safari)
    // don't reliably fire 'focus' when returning from a background tab.
    useEffect(() => {
      const handleFocus = () => {
        // Skip during post-login callback to avoid racing with cookie propagation
        if (isPostLoginCallback(window.location.pathname, window.location.search)) return;

        // Same document scoping as useAuthStatus's marker read (POR-433): sessionStorage outlives
        // the page that wrote the stamp, so an unscoped read suppresses the *next* document too.
        try {
          const lastLogout = Number(sessionStorage.getItem('__last_logout_ts')) || 0;
          const loggedOutThisDocument = lastLogout >= Math.floor(performance.timeOrigin);
          if (loggedOutThisDocument && Date.now() - lastLogout < RECENT_LOGOUT_SUPPRESSION_MS) return;
        } catch {
          // An unreadable stamp suppresses nothing — revalidating is the safe direction.
        }

        // Skip on public routes — no auth check needed
        if (isPublicRoute(window.location.pathname)) return;

        // cancelRefetch: false for the same reason the gate's recheck sets it — the default would
        // abort an in-flight /auth/me the gate is still waiting on.
        void queryClient.invalidateQueries({ queryKey: authQueryKeys.me() }, { cancelRefetch: false });
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') handleFocus();
      };

      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }, [queryClient]);

    const topLevelLinks: SidebarLinkProps[] = useMemo(
      () => [
        {
          children: t('stu'),
          icon: 'notebook-pen',
          preload: 'intent',
          to: '/studies',
        },
        {
          children: t('runs'),
          icon: 'flask-conical',
          preload: 'intent',
          to: '/instrument_runs',
        },
        {
          children: t('sam'),
          icon: 'microscope',
          roles: [SUPER_ADMIN_ROLE],
          preload: 'intent',
          to: '/samples',
        },
        {
          children: t('use'),
          icon: 'user-round-cog',
          roles: adminRoles,
          to: '/users',
        },
        {
          children: t('instr'),
          icon: VoyagerIcon,
          roles: adminRoles,
          preload: 'intent',
          to: '/instruments',
        },
        {
          children: t('org'),
          icon: OrgIcon,
          roles: adminRoles,
          to: '/organizations',
        },
        // { children: t('fle'), icon: 'computer', to: '/fleet' },
      ],
      [t],
    );
    const sidebarRef = usePanelRef();
    // elementRef gives the outer wrapper (overflow: visible); its first child
    // is the actual scrollable element, stamped with a stable attribute
    // selector for TanStack Router's scroll restoration to target.
    const mainPanelInnerRef = useCallback((el: HTMLDivElement | null) => {
      const scrollable = el?.firstElementChild as HTMLDivElement | null;
      scrollable?.setAttribute('data-scroll-restoration-id', 'main-scrollable-area');
    }, []);

    const resizerDblClickHandler = useCallback(() => {
      return sidebarRef?.current?.isCollapsed() ? sidebarRef?.current?.expand() : sidebarRef?.current?.collapse();
    }, [sidebarRef]);

    const handleLayoutChange = useCallback(() => {
      if (!didMountRef.current) {
        didMountRef.current = true;
        return;
      }

      setIsDragging(true);

      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }

      dragTimeoutRef.current = setTimeout(() => {
        setIsDragging(false);
      }, 100);
    }, []);

    useEffect(() => {
      return () => {
        if (dragTimeoutRef.current) {
          clearTimeout(dragTimeoutRef.current);
        }
      };
    }, []);

    const handleSeparatorMouseEnter = useCallback(() => {
      setIsHovering(true);
    }, []);
    const handleSeparatorMouseLeave = useCallback(() => {
      setIsHovering(false);
    }, []);

    return (
      <>
        <HeadContent />

        <Scripts />

        <Layout disabled={!user} onLayoutChange={handleLayoutChange}>
          {showSidebar && (
            <>
              <Sidebar disabled={sidebarDisabled} links={topLevelLinks} sidebarRef={sidebarRef} />
              <Separator
                className={clsx(
                  pageStyles.resizeHandle,
                  isDragging && pageStyles.resizing,
                  isHovering && pageStyles.resizeHover,
                )}
                onDoubleClick={resizerDblClickHandler}
                onMouseEnter={handleSeparatorMouseEnter}
                onMouseLeave={handleSeparatorMouseLeave}
              />
            </>
          )}

          <Panel
            elementRef={mainPanelInnerRef}
            className={clsx(
              mainContent,
              showSidebar && mainContentWithSidebar,
              globalStyles.scrollbars,
              'maincontent-panel',
            )}
          >
            {mainPanelContent(gateState, postLoginCallback, tLogin('loading.verifying_session'))}
          </Panel>
        </Layout>

        <Toaster toastOptions={toastOptions} />

        {import.meta.env.VITE_ENABLE_DEVTOOLS === 'true' && (
          <TanStackDevtools config={tanstackConfig} plugins={tanstackPlugins} />
        )}
      </>
    );
  },
  // No longer passing selectedOrganization into context; access it directly from the store where needed
  head: () => ({
    meta: [
      {
        name: 'Nautilus Biotechnology - Customer Portal',
        content: 'App for interacting with instruments.',
      },
      {
        title: 'Nautilus Biotechnology - Customer Portal',
      },
    ],
  }),
});
